import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';
import { useAuthTenant } from 'store/auth';
import { getSelectedContent } from 'utils/select_utils';

import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useTheme,
} from '@mui/material';
import { Column, Row } from 'Components/Grid';
import { ButtonForm } from 'Components/Form/styles';
import { DotsLoader } from 'Components/DotsLoader';

import { getAreasShort } from 'services/areas-service';
import { getProcessesShort } from 'services/processes-service';
import { getSystemsShort } from 'services/systems-service';
import { getStyles, MenuProps } from './helpers';
import { InputLoader } from 'Components/PageLoader/Input';

export const Vinculos = ({ handleBack, handleComplete, control, handleSubmit, isLoading }) => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);
  const theme = useTheme();

  const onSubmit = () => {
    handleComplete();
  };

  const queries = useQueries([
    {
      queryKey: ['getAreas'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAreasShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getProcesses'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getProcessesShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getSystems'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getSystemsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: areas, isLoading: areaLoading, isError: areaError },
    { data: processes, isLoading: processLoading, isError: processError },
    { data: systems, isLoading: systemLoading, isError: systemError },
  ] = queries;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!areaLoading && !areaError && (
            <Controller
              control={control}
              name="areas"
              render={({ field: { onChange, value } }) => {
                const safeValue = Array.isArray(value) ? value : [];

                const visibleAreas = areas?.filter(a => !a.archived) ?? [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                    <InputLabel id="areas-label" shrink>
                      {t('asm:assets.form.linkedAreas')}
                    </InputLabel>

                    <Select
                      labelId="areas-label"
                      id="areas"
                      multiple
                      notched
                      value={getSelectedContent(visibleAreas, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="areas-chip" label={t('asm:assets.form.linkedAreas')} />}
                      renderValue={(selectedAreas) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // não abrir o menu ao interagir com chips
                        >
                          {selectedAreas?.map((selectedValue) => (
                            <Chip
                              key={`area-chip-${selectedValue._id}`}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove só este
                              }}
                              sx={{ width: 'fit-content' }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleAreas.map((area) => (
                        <MenuItem
                          key={area._id}
                          value={area}
                          style={{
                            // passa array de IDs selecionados para destacar corretamente
                            ...getStyles(area._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {area.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            />
          )}

          {areaLoading && !areaError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="areas-label" shrink>
                {t('asm:assets.form.linkedAreas')}
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!processLoading && !processError && (
            <Controller
              control={control}
              name="processes"
              render={({ field: { onChange, value }, fieldState }) => {
                const safeValue = Array.isArray(value) ? value : [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                // opcional: esconder arquivados da lista/visual
                const visibleProcesses = processes?.filter(p => !p.archived) ?? [];

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="processes-label" shrink>
                      {t('asm:assets.form.linkedProcesses')}
                    </InputLabel>

                    <Select
                      labelId="processes-label"
                      id="processes"
                      multiple
                      notched
                      value={getSelectedContent(visibleProcesses, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="processes-chip" label={t('asm:assets.form.linkedProcesses')} />}
                      renderValue={(selectedProcesses) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // evita abrir o menu ao interagir com chips
                        >
                          {selectedProcesses?.map((selectedValue) => (
                            <Chip
                              key={`process-chip-${selectedValue._id}`}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove só este
                              }}
                              sx={{ width: 'fit-content' }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleProcesses.map((item) => (
                        <MenuItem
                          key={`process-${item._id}`}
                          value={item}
                          style={{
                            // passa array de IDs selecionados para destacar corretamente
                            ...getStyles(item._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText>
                      {fieldState.error ? fieldState.error.message : ''}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {processLoading && !processError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="processes-label" shrink>
                {t('asm:assets.form.linkedProcesses')}
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!systemLoading && !systemError && (
            <Controller
              control={control}
              name="systems"
              render={({ field: { onChange, value }, fieldState }) => {
                const safeValue = Array.isArray(value) ? value : [];

                const visibleSystems = systems?.filter(s => !s.archived) ?? [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="systems-label" shrink>
                      {t('asm:assets.form.linkedSystems')}
                    </InputLabel>

                    <Select
                      labelId="systems-label"
                      id="systems"
                      multiple
                      notched
                      value={getSelectedContent(visibleSystems, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="systems-chip" label={t('asm:assets.form.linkedSystems')} />}
                      renderValue={(selectedSystems) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {selectedSystems?.map((selectedValue) => (
                            <Chip
                              key={selectedValue._id}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue);
                              }}
                              sx={{ width: 'fit-content' }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleSystems.map((item) => (
                        <MenuItem
                          key={item._id}
                          value={item}
                          style={{
                            // passa array de IDs selecionados para destacar corretamente
                            ...getStyles(item._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText>
                      {fieldState.error ? fieldState.error.message : ''}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {systemLoading && !systemError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="systems-label" shrink>
                {t('asm:assets.form.linkedSystems')}
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="end" alignItems="center">
        <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
          <ButtonForm secondary id="cancel" label={t('asm:assets.form.cancel')} onClick={handleBack}></ButtonForm>
          <ButtonForm label={isLoading ? <DotsLoader /> : t('asm:assets.form.save')} type="submit" />
        </Column>
      </Row>
    </form>
  );
};
